'use client';

import Script from 'next/script';
import { useEffect, useRef, useState } from 'react';
import { getTurnstileSiteKey } from '@/lib/newsletter/turnstile';

/**
 * `always` shows the checkbox widget. `interaction-only` stays invisible unless
 * Cloudflare needs the visitor to interact.
 */
export type TurnstileAppearance = 'always' | 'interaction-only';

type TurnstileRenderOptions = {
  sitekey: string;
  theme: 'light';
  appearance: TurnstileAppearance;
  action: string;
  callback: (token: string) => void;
  'expired-callback': () => void;
  'error-callback': () => void;
};

type TurnstileApi = {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SITE_KEY = getTurnstileSiteKey();

/**
 * Cloudflare Turnstile widget. The parent form receives the token through
 * `onToken` and sends it with the submit. `resetSignal` forces a new challenge:
 * Turnstile tokens are single-use, so any rejected submit has to mint a fresh one.
 *
 * `action` is echoed back by Cloudflare's siteverify, so whoever verifies the
 * token can check it was minted for this form.
 */
export default function TurnstileField({
  onToken,
  resetSignal,
  action = 'newsletter_subscribe',
  appearance = 'always',
}: {
  onToken: (token: string | null) => void;
  resetSignal: number;
  action?: string;
  appearance?: TurnstileAppearance;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!SITE_KEY) return;

    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;
    let poll: number | undefined;

    const mount = () => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      if (widgetIdRef.current) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
      setFailed(false);
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        theme: 'light',
        appearance,
        action,
        callback: token => {
          if (cancelled) return;
          onTokenRef.current(token);
        },
        'expired-callback': () => {
          if (cancelled) return;
          onTokenRef.current(null);
        },
        // Removing the widget after a failed signup also fires this. That
        // teardown is not a load failure, so ignore it once this effect ends.
        'error-callback': () => {
          if (cancelled) return;
          onTokenRef.current(null);
          setFailed(true);
        },
      });
    };

    if (window.turnstile) {
      mount();
    } else {
      poll = window.setInterval(() => {
        if (!window.turnstile) return;
        window.clearInterval(poll);
        mount();
      }, 50);
    }

    return () => {
      cancelled = true;
      if (poll !== undefined) window.clearInterval(poll);
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [resetSignal, action, appearance]);

  if (!SITE_KEY) {
    return (
      <p className="text-[13px] font-medium text-red-500">
        Bot check is not configured.
      </p>
    );
  }

  return (
    <div className="flex w-full flex-col items-center">
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
        strategy="afterInteractive"
      />
      {/* The invisible mode reserves no space: it only grows when Cloudflare
          asks the visitor to interact. */}
      <div
        ref={containerRef}
        className={appearance === 'always' ? 'min-h-[65px]' : undefined}
      />
      {failed && (
        <p className="text-[13px] font-medium text-red-500">
          Bot check failed to load. Refresh the page and try again.
        </p>
      )}
    </div>
  );
}
